import is from '@sindresorhus/is';

import { getSetupEntryPath } from '~/feature/job-architecture-v2';
import {
    CREATE_ARCHITECTURE_SEARCH_PARAM,
    CREATE_ARCHITECTURE_SEARCH_VALUE,
    JOB_ARCHITECTURE_ID_SEARCH_PARAM,
    LEVEL_MAPPING_RUN_SEARCH_PARAM,
    LEVEL_MAPPING_SEARCH_PARAM,
    POSITION_SETUP_ROUTE_SEGMENT,
} from '~/feature/position-management';
import type { TranslationKey } from '~/shared/internationalization/translation';

import type {
    CompanyJourney,
    JourneyArea,
    JourneyAreaCode,
    JourneyGuideStep,
    JourneyMilestone,
    JourneyMilestoneCode,
    JourneyNextStep,
    JourneyState,
    JourneyStepCode,
    JourneyStepScope,
} from '../data-contract/company-home-journey';
import { journeyAreaCodeSchema } from '../data-contract/company-home-journey';
import type { CompanyHomeLinks, HomeLink } from './home-links';

export type JourneyCopy = {
    readonly count?: number | null;
    readonly dates?: Readonly<Record<string, string>>;
    readonly key: TranslationKey;
    readonly values?: Readonly<Record<string, number | string | null>>;
};

export type JourneyCtaAction = {
    readonly kind: 'cta';
    readonly label: JourneyCopy;
    readonly page: HomeLink;
    readonly path: string;
};

export type JourneySetupAction = {
    readonly cta: JourneyCtaAction;
    readonly jobArchitectureId: string;
    readonly kind: 'setup';
    readonly label: JourneyCopy | undefined;
    readonly step: JourneyGuideStep;
};

export type JourneyConnectAction = {
    readonly architectureId: string;
    readonly jobArchitectureId: string;
    readonly kind: 'connect';
    readonly label: JourneyCopy;
};

export type JourneyPromptAction = {
    readonly isRepeatable: boolean;
    readonly kind: 'prompt';
    readonly label: JourneyCopy;
    readonly prompt: JourneyCopy;
};

export type JourneyAction = JourneyConnectAction | JourneyCtaAction | JourneyPromptAction | JourneySetupAction;

export type JourneyGuide = {
    readonly action: JourneyAction | undefined;
    readonly area: JourneyAreaCode | undefined;
    readonly isStep: boolean;
    readonly milestone: JourneyMilestoneCode | undefined;
    readonly reason: JourneyCopy | undefined;
    readonly skippable: boolean;
    readonly why: JourneyCopy | undefined;
};

export const JOURNEY_AREAS: readonly JourneyAreaCode[] = journeyAreaCodeSchema.options;

export const areaOf = (journey: CompanyJourney, area: JourneyAreaCode): JourneyArea | undefined =>
    journey.areas.find((candidate) => candidate.area === area);

const SKIP_AREAS: Readonly<Partial<Record<JourneyStepCode, JourneyAreaCode>>> = {
    ASSIGN_REVIEWERS: 'WORK_ARCHITECTURE',
    LINK_POSITIONS: 'WORK_ARCHITECTURE',
    PUBLISH: 'WORK_ARCHITECTURE',
    REVIEW_LINK_SUGGESTIONS: 'WORK_ARCHITECTURE',
    REVIEW_MINE: 'WORK_ARCHITECTURE',
    UPLOAD_DOCUMENTS: 'KNOWLEDGE_BASE',
};

export const skippedInAreaOf = (
    area: JourneyAreaCode,
    skipped: readonly JourneyStepCode[],
): readonly JourneyStepCode[] => skipped.filter((code) => SKIP_AREAS[code] === area);

export const skippedAreaStepOf = (
    journey: CompanyJourney,
    area: JourneyAreaCode,
    skipped: readonly JourneyStepCode[],
): JourneyNextStep | undefined => {
    const step = areaOf(journey, area)?.nextStep;

    return is.nullOrUndefined(step) || !skipped.includes(step.code) || step.code === journey.nextStep?.code
        ? undefined
        : step;
};

export const areaStateOf = (journey: CompanyJourney, area: JourneyAreaCode): JourneyState =>
    areaOf(journey, area)?.state ?? 'HIDDEN';

export const visibleMilestonesOf = (journey: CompanyJourney, area: JourneyAreaCode): readonly JourneyMilestone[] =>
    (areaOf(journey, area)?.milestones ?? []).filter(({ state }) => state !== 'HIDDEN');

export const currentMilestoneOf = (journey: CompanyJourney, area: JourneyAreaCode): JourneyMilestone | undefined =>
    visibleMilestonesOf(journey, area).find(({ state }) => state !== 'DONE');

export const isPositive = (value: number | null | undefined): value is number =>
    value !== null && value !== undefined && value > 0;

type StepPage = 'aiTransformation' | 'jobArchitecture' | 'knowledgeBase' | 'orgDashboard' | 'positionManagement';

type PromptSubject = 'connection' | 'jobArchitecture' | 'none' | 'positionArchitecture' | 'sourceJobArchitecture';

type PromptKeys = { readonly ask: TranslationKey; readonly text: TranslationKey };

type StepPrompt = {
    readonly isCounted: boolean;
    readonly isRepeatable: boolean;
    readonly keys: (step: JourneyNextStep) => PromptKeys | undefined;
    readonly needsPage: boolean;
    readonly subject: PromptSubject;
};

const always =
    (ask: TranslationKey, text: TranslationKey): StepPrompt['keys'] =>
    () => ({ ask, text });

const byScope =
    (ask: TranslationKey, texts: Readonly<Record<JourneyStepScope, TranslationKey>>): StepPrompt['keys'] =>
    (step) =>
        step.scope === null ? undefined : { ask, text: texts[step.scope] };

const STRUCTURE_PROMPT: PromptKeys = {
    ask: 'pages.company-home.journey.steps.finish-ja-setup.ask-structure',
    text: 'pages.company-home.journey.steps.finish-ja-setup.prompt-structure',
};

const SETUP_PROMPTS: Readonly<Partial<Record<JourneyGuideStep, PromptKeys>>> = {
    'career-framework': {
        ask: 'pages.company-home.journey.steps.finish-ja-setup.ask-career-framework',
        text: 'pages.company-home.journey.steps.finish-ja-setup.prompt-career-framework',
    },
    'company-information': {
        ask: 'pages.company-home.journey.steps.finish-ja-setup.ask-company-information',
        text: 'pages.company-home.journey.steps.finish-ja-setup.prompt-company-information',
    },
    'data-upload': STRUCTURE_PROMPT,
    'generate-job-architecture': STRUCTURE_PROMPT,
    'job-profile-template': {
        ask: 'pages.company-home.journey.steps.finish-ja-setup.ask-job-profile-template',
        text: 'pages.company-home.journey.steps.finish-ja-setup.prompt-job-profile-template',
    },
};

type StepCopy = {
    readonly connectToLabel?: TranslationKey;
    readonly label: TranslationKey;
    readonly page: StepPage;
    readonly prompt?: StepPrompt;
    readonly why?: TranslationKey;
};

const STEPS: Readonly<Record<JourneyStepCode, StepCopy>> = {
    ADD_LEVEL_TASKS: {
        label: 'pages.company-home.journey.steps.add-level-tasks.label',
        page: 'jobArchitecture',
        why: 'pages.company-home.journey.why.add-level-tasks',
    },
    ASSIGN_REVIEWERS: {
        label: 'pages.company-home.journey.steps.assign-reviewers.label',
        page: 'orgDashboard',
        prompt: {
            isCounted: true,
            isRepeatable: false,
            keys: byScope('pages.company-home.journey.steps.assign-reviewers.ask', {
                LEVELS: 'pages.company-home.journey.steps.assign-level-reviewers.prompt',
                ROLES: 'pages.company-home.journey.steps.assign-role-reviewers.prompt',
            }),
            needsPage: false,
            subject: 'jobArchitecture',
        },
        why: 'pages.company-home.journey.why.assign-reviewers',
    },
    CONNECT_JOB_ARCHITECTURE: {
        label: 'pages.company-home.journey.steps.connect-job-architecture.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: false,
            keys: (step) =>
                step.connectTo === null
                    ? {
                          ask: 'pages.company-home.journey.steps.connect-job-architecture.ask',
                          text: 'pages.company-home.journey.steps.connect-job-architecture.prompt-choose',
                      }
                    : {
                          ask: 'pages.company-home.journey.steps.connect-job-architecture.ask-named',
                          text: 'pages.company-home.journey.steps.connect-job-architecture.prompt',
                      },
            needsPage: true,
            subject: 'connection',
        },
        why: 'pages.company-home.journey.why.connect-job-architecture',
    },
    CONTINUE_POSITION_SETUP: {
        label: 'pages.company-home.journey.steps.continue-position-setup.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: false,
            keys: always(
                'pages.company-home.journey.steps.continue-position-setup.ask',
                'pages.company-home.journey.steps.continue-position-setup.prompt',
            ),
            needsPage: true,
            subject: 'positionArchitecture',
        },
        why: 'pages.company-home.journey.why.continue-position-setup',
    },
    CREATE_JOB_ARCHITECTURE: {
        label: 'pages.company-home.journey.steps.create-job-architecture.label',
        page: 'jobArchitecture',
        prompt: {
            isCounted: false,
            isRepeatable: false,
            keys: always(
                'pages.company-home.journey.steps.create-job-architecture.ask',
                'pages.company-home.journey.steps.create-job-architecture.prompt',
            ),
            needsPage: true,
            subject: 'none',
        },
        why: 'pages.company-home.journey.why.create-job-architecture',
    },
    CREATE_LEVELS: {
        label: 'pages.company-home.journey.steps.create-levels.label',
        page: 'jobArchitecture',
        prompt: {
            isCounted: true,
            isRepeatable: false,
            keys: always(
                'pages.company-home.journey.steps.create-levels.ask',
                'pages.company-home.journey.steps.create-levels.prompt',
            ),
            needsPage: false,
            subject: 'jobArchitecture',
        },
        why: 'pages.company-home.journey.why.create-levels',
    },
    CREATE_POSITION_ARCHITECTURE: {
        connectToLabel: 'pages.company-home.journey.steps.create-position-architecture.label-from',
        label: 'pages.company-home.journey.steps.create-position-architecture.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: false,
            keys: (step) =>
                step.connectTo === null
                    ? {
                          ask: 'pages.company-home.journey.steps.create-position-architecture.ask',
                          text: 'pages.company-home.journey.steps.create-position-architecture.prompt',
                      }
                    : {
                          ask: 'pages.company-home.journey.steps.create-position-architecture.ask-from',
                          text: 'pages.company-home.journey.steps.create-position-architecture.prompt-from',
                      },
            needsPage: true,
            subject: 'sourceJobArchitecture',
        },
        why: 'pages.company-home.journey.why.create-position-architecture',
    },
    CREATE_ROLES: {
        label: 'pages.company-home.journey.steps.create-roles.label',
        page: 'jobArchitecture',
        prompt: {
            isCounted: false,
            isRepeatable: false,
            keys: always(
                'pages.company-home.journey.steps.create-roles.ask',
                'pages.company-home.journey.steps.create-roles.prompt',
            ),
            needsPage: false,
            subject: 'jobArchitecture',
        },
        why: 'pages.company-home.journey.why.create-roles',
    },
    DEFINE: {
        label: 'pages.company-home.journey.steps.define.label',
        page: 'aiTransformation',
        why: 'pages.company-home.journey.why.define',
    },
    FINISH_JA_SETUP: {
        label: 'pages.company-home.journey.steps.finish-ja-setup.label',
        page: 'jobArchitecture',
        prompt: {
            isCounted: false,
            isRepeatable: false,
            keys: (step) => (step.guideStep === null ? undefined : SETUP_PROMPTS[step.guideStep]),
            needsPage: true,
            subject: 'jobArchitecture',
        },
        why: 'pages.company-home.journey.why.finish-ja-setup',
    },
    FOLLOW: { label: 'pages.company-home.journey.steps.follow.label', page: 'aiTransformation' },
    JA_GENERATION_FAILED: {
        label: 'pages.company-home.journey.steps.ja-generation-failed.label',
        page: 'jobArchitecture',
        prompt: {
            isCounted: false,
            isRepeatable: true,
            keys: always(
                'pages.company-home.journey.steps.ja-generation-failed.ask',
                'pages.company-home.journey.steps.ja-generation-failed.prompt',
            ),
            needsPage: true,
            subject: 'jobArchitecture',
        },
    },
    LINK_POSITIONS: {
        label: 'pages.company-home.journey.steps.link-positions.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: true,
            keys: always(
                'pages.company-home.journey.steps.link-positions.ask',
                'pages.company-home.journey.steps.link-positions.prompt',
            ),
            needsPage: true,
            subject: 'positionArchitecture',
        },
        why: 'pages.company-home.journey.why.link-positions',
    },
    PRIORITIZE: {
        label: 'pages.company-home.journey.steps.prioritize.label',
        page: 'aiTransformation',
        why: 'pages.company-home.journey.why.prioritize',
    },
    PUBLISH: {
        label: 'pages.company-home.journey.steps.publish.label',
        page: 'orgDashboard',
        prompt: {
            isCounted: true,
            isRepeatable: false,
            keys: byScope('pages.company-home.journey.steps.publish.ask', {
                LEVELS: 'pages.company-home.journey.steps.publish-levels.prompt',
                ROLES: 'pages.company-home.journey.steps.publish-roles.prompt',
            }),
            needsPage: false,
            subject: 'jobArchitecture',
        },
        why: 'pages.company-home.journey.why.publish',
    },
    RESTART_LEVEL_MAPPING: {
        label: 'pages.company-home.journey.steps.restart-level-mapping.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: true,
            keys: always(
                'pages.company-home.journey.steps.restart-level-mapping.ask',
                'pages.company-home.journey.steps.restart-level-mapping.prompt',
            ),
            needsPage: true,
            subject: 'positionArchitecture',
        },
    },
    RETRY_LINK_APPLY: {
        label: 'pages.company-home.journey.steps.retry-link-apply.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: true,
            keys: always(
                'pages.company-home.journey.steps.retry-link-apply.ask',
                'pages.company-home.journey.steps.retry-link-apply.prompt',
            ),
            needsPage: true,
            subject: 'positionArchitecture',
        },
    },
    RETRY_POSITION_PLAN: {
        label: 'pages.company-home.journey.steps.retry-position-plan.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: true,
            keys: always(
                'pages.company-home.journey.steps.retry-position-plan.ask',
                'pages.company-home.journey.steps.retry-position-plan.prompt',
            ),
            needsPage: true,
            subject: 'positionArchitecture',
        },
    },
    REVIEW_LINK_SUGGESTIONS: {
        label: 'pages.company-home.journey.steps.review-link-suggestions.label',
        page: 'positionManagement',
        prompt: {
            isCounted: false,
            isRepeatable: true,
            keys: always(
                'pages.company-home.journey.steps.review-link-suggestions.ask',
                'pages.company-home.journey.steps.review-link-suggestions.prompt',
            ),
            needsPage: true,
            subject: 'positionArchitecture',
        },
        why: 'pages.company-home.journey.why.review-link-suggestions',
    },
    REVIEW_MINE: {
        label: 'pages.company-home.journey.steps.review-mine.label',
        page: 'orgDashboard',
        prompt: {
            isCounted: true,
            isRepeatable: false,
            keys: byScope('pages.company-home.journey.steps.review-mine.ask', {
                LEVELS: 'pages.company-home.journey.steps.review-mine-levels.prompt',
                ROLES: 'pages.company-home.journey.steps.review-mine-roles.prompt',
            }),
            needsPage: false,
            subject: 'jobArchitecture',
        },
        why: 'pages.company-home.journey.why.review-mine',
    },
    UPLOAD_DOCUMENTS: {
        label: 'pages.company-home.journey.steps.upload-documents.label',
        page: 'knowledgeBase',
        prompt: {
            isCounted: false,
            isRepeatable: false,
            keys: always(
                'pages.company-home.journey.steps.upload-documents.ask',
                'pages.company-home.journey.steps.upload-documents.prompt',
            ),
            needsPage: true,
            subject: 'none',
        },
        why: 'pages.company-home.journey.why.upload-documents',
    },
};

export const JOB_ARCHITECTURE_PATH = '/job-architecture-v2';

const withSearch = (path: string, params: readonly (readonly [string, string | undefined])[]): string => {
    const search = new URLSearchParams();

    for (const [name, value] of params) {
        if (value !== undefined) {
            search.append(name, value);
        }
    }

    const query = search.toString();

    return query === '' ? path : `${path}?${query}`;
};

export const jobArchitectureBoardPath = (jobArchitectureId: string): string =>
    withSearch(`${JOB_ARCHITECTURE_PATH}/${jobArchitectureId}/overview`, [['tab', 'board']]);

export const positionArchitecturePath = (page: HomeLink, architectureId: string): string =>
    withSearch(page.path, [['architectureId', architectureId]]);

export const reviewPath = (page: HomeLink): string => withSearch(page.path, [['tab', 'review']]);

export const levelMappingPath = (
    page: Pick<HomeLink, 'path'>,
    architectureId: string | undefined,
    runId?: string,
): string =>
    withSearch(page.path, [
        ['architectureId', architectureId],
        [LEVEL_MAPPING_SEARCH_PARAM, 'true'],
        [LEVEL_MAPPING_RUN_SEARCH_PARAM, runId],
    ]);

const jobArchitecturePath = (code: JourneyStepCode, journey: CompanyJourney, page: HomeLink): string => {
    const jobArchitectureId = journey.jobArchitecture?.id;

    if (code === 'CREATE_JOB_ARCHITECTURE') {
        return JOB_ARCHITECTURE_PATH;
    }

    if (jobArchitectureId === undefined) {
        return page.path;
    }

    return code === 'FINISH_JA_SETUP'
        ? getSetupEntryPath(jobArchitectureId)
        : jobArchitectureBoardPath(jobArchitectureId);
};

const aiTransformationPath = (code: JourneyStepCode, journey: CompanyJourney, page: HomeLink): string => {
    const architectureId: readonly [string, string | undefined] = ['architectureId', journey.positionArchitecture?.id];

    switch (code) {
        case 'PRIORITIZE':
            return withSearch(page.path, [
                architectureId,
                ['view', 'positions'],
                ['scoreLevel', 'HIGH'],
                ['scoreLevel', 'VERY_HIGH'],
            ]);
        case 'DEFINE':
            return withSearch(page.path, [architectureId, ['view', 'positions'], ['layout', 'kanban']]);
        default:
            return withSearch(page.path, [architectureId]);
    }
};

const createPositionArchitecturePath = (step: JourneyNextStep, page: HomeLink): string =>
    step.connectTo === null
        ? page.path
        : withSearch(page.path, [
              [CREATE_ARCHITECTURE_SEARCH_PARAM, CREATE_ARCHITECTURE_SEARCH_VALUE],
              [JOB_ARCHITECTURE_ID_SEARCH_PARAM, step.connectTo.id],
          ]);

const positionManagementPath = (step: JourneyNextStep, journey: CompanyJourney, page: HomeLink): string => {
    const architectureId: readonly [string, string | undefined] = ['architectureId', journey.positionArchitecture?.id];

    switch (step.code) {
        case 'CREATE_POSITION_ARCHITECTURE':
            return createPositionArchitecturePath(step, page);
        case 'CONTINUE_POSITION_SETUP':
        case 'RETRY_POSITION_PLAN':
            return withSearch(`${page.path}/${POSITION_SETUP_ROUTE_SEGMENT}`, [architectureId]);
        case 'CONNECT_JOB_ARCHITECTURE':
            return withSearch(page.path, [architectureId]);
        case 'REVIEW_LINK_SUGGESTIONS':
        case 'RETRY_LINK_APPLY':
            return levelMappingPath(page, journey.positionArchitecture?.id, journey.facts.levelLinks.run?.id);
        default:
            return levelMappingPath(page, journey.positionArchitecture?.id);
    }
};

const stepPath = (step: JourneyNextStep, journey: CompanyJourney, page: HomeLink): string => {
    switch (STEPS[step.code].page) {
        case 'knowledgeBase':
            return page.path;
        case 'orgDashboard':
            return reviewPath(page);
        case 'jobArchitecture':
            return jobArchitecturePath(step.code, journey, page);
        case 'aiTransformation':
            return aiTransformationPath(step.code, journey, page);
        case 'positionManagement':
            return positionManagementPath(step, journey, page);
    }
};

const matchingCopy = (run: CompanyJourney['facts']['levelLinks']['run']): JourneyCopy | undefined =>
    run === null || run.unitsDone === null || !isPositive(run.unitsTotal)
        ? undefined
        : {
              key: 'pages.company-home.journey.figures.matching',
              values: { done: run.unitsDone, total: run.unitsTotal },
          };

const publicationProcessingCopy = (guideStep: JourneyGuideStep | null): JourneyCopy => {
    switch (guideStep) {
        case 'generate-roles':
            return { key: 'pages.company-home.journey.headline.roles-generating' };
        case 'generate-levels':
            return { key: 'pages.company-home.journey.headline.levels-generating' };
        default:
            return { key: 'pages.company-home.journey.headline.job-architecture-generating' };
    }
};

export const processingCopy = (journey: CompanyJourney, code: JourneyMilestoneCode): JourneyCopy => {
    const { aiTransformation, jobArchitecture, knowledgeBase, levelLinks } = journey.facts;

    switch (code) {
        case 'DOCUMENTS':
            return { count: knowledgeBase.processing, key: 'pages.company-home.journey.facts.reading' };
        case 'POSITIONS':
            return { key: 'pages.company-home.journey.headline.positions-generating' };
        case 'JOB_ARCHITECTURE_SETUP':
        case 'JOB_ARCHITECTURE_CONNECTED':
            return { key: 'pages.company-home.journey.headline.job-architecture-generating' };
        case 'JOB_ARCHITECTURE_PUBLICATION':
            return publicationProcessingCopy(jobArchitecture.guideStep);
        case 'POSITION_LINKS':
            return matchingCopy(levelLinks.run) ?? { key: 'pages.company-home.journey.headline.level-matching' };
        case 'TASK_ASSESSMENT':
        case 'PRIORITIZATION':
        case 'DEFINITION':
            return { count: aiTransformation.total, key: 'pages.company-home.journey.headline.assessing' };
    }
};

const lockedCopy = (area: JourneyAreaCode): JourneyCopy =>
    area === 'AI_TRANSFORMATION'
        ? { key: 'pages.company-home.journey.locked.ai-transformation' }
        : { key: 'pages.company-home.journey.locked.job-architecture' };

const milestoneStatus = (
    journey: CompanyJourney,
    area: JourneyAreaCode,
    milestone: JourneyMilestone,
): JourneyCopy | undefined => {
    switch (milestone.state) {
        case 'ACTION':
            return { key: 'pages.company-home.journey.status.action' };
        case 'FAILED':
            return { key: 'pages.company-home.journey.status.failed' };
        case 'NOT_STARTED':
            return { key: 'pages.company-home.journey.status.not-started' };
        case 'LOCKED':
            return lockedCopy(area);
        case 'WAITING':
            return { key: 'pages.company-home.journey.status.waiting' };
        case 'PROCESSING':
            return processingCopy(journey, milestone.code);
        case 'DONE':
        case 'HIDDEN':
            return undefined;
    }
};

const areaStatus = (journey: CompanyJourney, area: JourneyAreaCode): JourneyCopy | undefined => {
    const current = currentMilestoneOf(journey, area);

    return current === undefined ? undefined : milestoneStatus(journey, area, current);
};

const POSITION_SETUP_STEPS: ReadonlySet<JourneyStepCode> = new Set([
    'CREATE_POSITION_ARCHITECTURE',
    'CONTINUE_POSITION_SETUP',
    'RETRY_POSITION_PLAN',
]);

const processingPassKey = (passed: JourneyMilestoneCode, step: JourneyNextStep): TranslationKey => {
    switch (passed) {
        case 'DOCUMENTS':
            return POSITION_SETUP_STEPS.has(step.code)
                ? 'pages.company-home.journey.passing.documents-reading-positions'
                : 'pages.company-home.journey.passing.documents-reading';
        case 'POSITIONS':
            return 'pages.company-home.journey.passing.positions-generating';
        case 'POSITION_LINKS':
            return 'pages.company-home.journey.passing.level-matching';
        default:
            return 'pages.company-home.journey.passing.job-architecture-generating';
    }
};

const passedMilestoneOf = (journey: CompanyJourney, step: JourneyNextStep): JourneyMilestone | undefined => {
    const milestones = JOURNEY_AREAS.flatMap((area) => visibleMilestonesOf(journey, area));
    const index = milestones.findIndex(({ code }) => code === step.milestone);

    return milestones
        .slice(0, Math.max(index, 0))
        .reverse()
        .find(({ state }) => state === 'PROCESSING' || state === 'WAITING');
};

const REVIEW_GUIDE_STEPS: ReadonlySet<JourneyGuideStep> = new Set(['review-publish-roles', 'review-publish-levels']);

const OPEN_REVIEW_GUIDE_STEPS: ReadonlySet<JourneyGuideStep> = new Set([
    'assign-role-reviewers',
    'review-publish-roles',
    'assign-level-reviewers',
    'review-publish-levels',
]);

const isPassingGuideReview = (journey: CompanyJourney, step: JourneyNextStep): boolean => {
    const held = journey.facts.jobArchitecture.guideStep;

    return step.guideStep !== null && held !== null && held !== step.guideStep && REVIEW_GUIDE_STEPS.has(held);
};

const POSITION_MILESTONES: ReadonlySet<JourneyMilestoneCode> = new Set([
    'POSITIONS',
    'JOB_ARCHITECTURE_CONNECTED',
    'POSITION_LINKS',
]);

const OPEN_PUBLICATION_STATES: ReadonlySet<JourneyState> = new Set(['ACTION', 'WAITING']);

const isJobArchitecturePublicationOpen = (journey: CompanyJourney): boolean => {
    const held = journey.facts.jobArchitecture.guideStep;
    const publication = visibleMilestonesOf(journey, 'WORK_ARCHITECTURE').find(
        ({ code }) => code === 'JOB_ARCHITECTURE_PUBLICATION',
    );

    return (
        (publication !== undefined && OPEN_PUBLICATION_STATES.has(publication.state)) ||
        (held !== null && OPEN_REVIEW_GUIDE_STEPS.has(held))
    );
};

const isPassingJobArchitecturePublication = (journey: CompanyJourney, step: JourneyNextStep): boolean =>
    journey.jobArchitecture !== null &&
    POSITION_MILESTONES.has(step.milestone) &&
    isJobArchitecturePublicationOpen(journey);

const passingReason = (journey: CompanyJourney, step: JourneyNextStep): JourneyCopy | undefined => {
    const passed = passedMilestoneOf(journey, step);

    if (passed?.state === 'PROCESSING') {
        return { key: processingPassKey(passed.code, step) };
    }

    if (isPassingJobArchitecturePublication(journey, step)) {
        return { key: 'pages.company-home.journey.passing.job-architecture-review-later' };
    }

    if (passed?.state === 'WAITING') {
        return {
            key:
                step.area === 'WORK_ARCHITECTURE'
                    ? 'pages.company-home.journey.passing.reviews-pending-guide'
                    : 'pages.company-home.journey.passing.reviews-pending',
        };
    }

    return isPassingGuideReview(journey, step)
        ? { key: 'pages.company-home.journey.passing.reviews-pending-guide' }
        : undefined;
};

const ACTIVE_STATES: ReadonlySet<JourneyState> = new Set(['ACTION', 'FAILED', 'PROCESSING', 'WAITING']);

const summaryAction = (journey: CompanyJourney): JourneyPromptAction | undefined =>
    journey.jobArchitecture === null
        ? undefined
        : {
              isRepeatable: false,
              kind: 'prompt',
              label: { key: 'pages.company-home.journey.summary.ask', values: { name: journey.jobArchitecture.title } },
              prompt: {
                  key: 'pages.company-home.journey.summary.prompt',
                  values: { name: journey.jobArchitecture.title },
              },
          };

const guideWithoutStep = (
    journey: CompanyJourney,
    canUseChat: boolean,
    skipped: readonly JourneyStepCode[],
): JourneyGuide => {
    const unskippedAreas = JOURNEY_AREAS.filter(
        (candidate) => skippedAreaStepOf(journey, candidate, skipped) === undefined,
    );
    const summary = canUseChat ? summaryAction(journey) : undefined;
    const area =
        summary !== undefined && areaStateOf(journey, 'WORK_ARCHITECTURE') !== 'HIDDEN'
            ? 'WORK_ARCHITECTURE'
            : (unskippedAreas.find((candidate) => ACTIVE_STATES.has(areaStateOf(journey, candidate))) ??
              unskippedAreas.find((candidate) => areaStatus(journey, candidate) !== undefined));

    return {
        action: area === 'WORK_ARCHITECTURE' ? summary : undefined,
        area,
        isStep: false,
        milestone: area === undefined ? undefined : currentMilestoneOf(journey, area)?.code,
        reason: area === undefined ? undefined : areaStatus(journey, area),
        skippable: false,
        why: undefined,
    };
};

const connectAction = (journey: CompanyJourney, step: JourneyNextStep): JourneyConnectAction | undefined =>
    step.code !== 'CONNECT_JOB_ARCHITECTURE' || step.connectTo === null || journey.positionArchitecture === null
        ? undefined
        : {
              architectureId: journey.positionArchitecture.id,
              jobArchitectureId: step.connectTo.id,
              kind: 'connect',
              label: { key: 'pages.company-home.journey.connect', values: { name: step.connectTo.title } },
          };

const isDocumentUpload = (step: JourneyNextStep): boolean =>
    step.code === 'FINISH_JA_SETUP' && step.guideStep === 'data-upload';

export const journeyStepLabel = (journey: CompanyJourney, step: JourneyNextStep): JourneyCopy => {
    if (isDocumentUpload(step)) {
        return { key: 'pages.company-home.journey.steps.upload-documents.label' };
    }

    const { connectToLabel, label } = STEPS[step.code];

    return connectToLabel === undefined || step.connectTo === null
        ? { key: label, values: { name: journey.jobArchitecture?.title ?? null } }
        : { key: connectToLabel, values: { jobArchitecture: step.connectTo.title } };
};

const whyOf = (step: JourneyNextStep): JourneyCopy | undefined => {
    const key = isDocumentUpload(step) ? 'pages.company-home.journey.why.setup-documents' : STEPS[step.code].why;

    return key === undefined ? undefined : { key };
};

const ctaAction = (
    journey: CompanyJourney,
    step: JourneyNextStep,
    links: CompanyHomeLinks,
): JourneyAction | undefined => {
    const page = links[STEPS[step.code].page];

    if (page === undefined) {
        return undefined;
    }

    const cta: JourneyCtaAction = {
        kind: 'cta',
        label: journeyStepLabel(journey, step),
        page,
        path: stepPath(step, journey, page),
    };

    return step.code === 'FINISH_JA_SETUP' && step.guideStep !== null && journey.jobArchitecture !== null
        ? {
              cta,
              jobArchitectureId: journey.jobArchitecture.id,
              kind: 'setup',
              label: isDocumentUpload(step) ? cta.label : undefined,
              step: step.guideStep,
          }
        : cta;
};

export const asksForSkippedDocuments = (journey: CompanyJourney): boolean =>
    journey.nextStep !== null &&
    isDocumentUpload(journey.nextStep) &&
    areaStateOf(journey, 'KNOWLEDGE_BASE') === 'ACTION';

const subjectValues = (
    journey: CompanyJourney,
    step: JourneyNextStep,
    subject: PromptSubject,
): Readonly<Record<string, string>> | undefined => {
    const { jobArchitecture, positionArchitecture } = journey;

    switch (subject) {
        case 'jobArchitecture':
            return jobArchitecture === null ? undefined : { name: jobArchitecture.title };
        case 'positionArchitecture':
            return positionArchitecture === null ? undefined : { name: positionArchitecture.name };
        case 'connection':
            return positionArchitecture === null
                ? undefined
                : {
                      name: positionArchitecture.name,
                      ...(step.connectTo === null ? {} : { jobArchitecture: step.connectTo.title }),
                  };
        case 'sourceJobArchitecture':
            return step.connectTo === null ? {} : { jobArchitecture: step.connectTo.title };
        case 'none':
            return {};
    }
};

const promptStepOf = (journey: CompanyJourney, step: JourneyNextStep): StepCopy =>
    asksForSkippedDocuments(journey) ? STEPS.UPLOAD_DOCUMENTS : STEPS[step.code];

const promptAction = (
    journey: CompanyJourney,
    step: JourneyNextStep,
    links: CompanyHomeLinks,
): JourneyPromptAction | undefined => {
    const { page, prompt } = promptStepOf(journey, step);
    const keys = prompt?.keys(step);
    const count = prompt?.isCounted === true ? step.count : undefined;
    const values = prompt === undefined ? undefined : subjectValues(journey, step, prompt.subject);
    const canOpenPage = prompt?.needsPage !== true || links[page] !== undefined;

    return prompt === undefined || keys === undefined || count === null || values === undefined || !canOpenPage
        ? undefined
        : {
              isRepeatable: prompt.isRepeatable,
              kind: 'prompt',
              label: { key: keys.ask, values },
              prompt: { key: keys.text, values, ...(count === undefined ? {} : { count }) },
          };
};

export const journeyGuide = ({
    canUseChat,
    journey,
    links,
    skipped = [],
}: {
    readonly canUseChat: boolean;
    readonly journey: CompanyJourney;
    readonly links: CompanyHomeLinks;
    readonly skipped?: readonly JourneyStepCode[];
}): JourneyGuide => {
    const { nextStep } = journey;

    if (nextStep === null) {
        return guideWithoutStep(journey, canUseChat, skipped);
    }

    const action =
        (canUseChat ? promptAction(journey, nextStep, links) : undefined) ??
        connectAction(journey, nextStep) ??
        ctaAction(journey, nextStep, links);
    const reason =
        skippedAreaStepOf(journey, nextStep.area, skipped) === undefined ? passingReason(journey, nextStep) : undefined;

    return {
        action,
        area: nextStep.area,
        isStep: true,
        milestone: nextStep.milestone,
        reason: action === undefined ? (reason ?? areaStatus(journey, nextStep.area)) : reason,
        skippable: nextStep.skippable && action !== undefined,
        why: canUseChat && action !== undefined ? whyOf(nextStep) : undefined,
    };
};
